import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { CropThumbnail } from '../../components/common/CropThumbnail';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { FORGE_CHAIN_OPTIONS, getForgeChainLabel } from '../../config/chains';
import {
  enumParam,
  stringParam,
  UrlParamCodec,
  useUrlFilterState,
} from '../../hooks/useUrlFilterState';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  EnforcementMode,
  ForgeDashboardChainInfo,
  MasterCropData,
  PersistedSaveWarning,
  SaveCheckModesResponse,
  SaveCheckTool,
  SaveCheckType,
  SaveWarningChainStats,
  SaveWarningEvidenceCrop,
  SaveWarningReviewOutcome,
  SaveWarningStatus,
} from '../../types';
import {
  ageSecondsFromTimestamp,
  formatRelativeAge,
  formatTimestamp,
} from '../../utils/forgeDashboardFormat';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';

const WARNINGS_PAGE_SIZE = 50;

const DEFAULT_SAVE_WARNING_CHAINS: ForgeDashboardChainInfo[] = FORGE_CHAIN_OPTIONS.map((chain) => ({
  chainId: chain.id,
  chainName: chain.label,
  timeZone: 'UTC',
}));

const SAVE_CHECK_TOOLS: readonly SaveCheckTool[] = ['master_label', 'remote_dc'];
const SAVE_CHECK_TYPES: readonly SaveCheckType[] = ['crop_placement', 'design_group'];
const SAVE_WARNING_STATUSES: readonly SaveWarningStatus[] = ['fired', 'acknowledged'];
const SAVE_WARNING_REVIEW_OUTCOMES: readonly SaveWarningReviewOutcome[] = [
  'correct_warning',
  'false_positive',
  'unsure',
];

const SAVE_CHECK_TOOL_LABELS: Record<SaveCheckTool, string> = {
  master_label: 'Master Label',
  remote_dc: 'Remote DC',
};

const SAVE_CHECK_TYPE_LABELS: Record<SaveCheckType, string> = {
  crop_placement: 'Crop placement',
  design_group: 'Design group',
};

const STATUS_LABELS: Record<SaveWarningStatus, string> = {
  fired: 'Fired',
  acknowledged: 'Acknowledged',
};

const STATUS_COLORS: Record<SaveWarningStatus, string> = {
  fired: '#eea337',
  acknowledged: '#2fa47a',
};

const REVIEW_OUTCOME_LABELS: Record<SaveWarningReviewOutcome, string> = {
  correct_warning: 'Valid warning',
  false_positive: 'False positive',
  unsure: 'Unsure',
};

const ENFORCEMENT_MODE_OPTIONS: Array<{ value: EnforcementMode; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'shadow', label: 'Shadow' },
  { value: 'enforce', label: 'Enforce' },
];

const headerCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--divider)',
  verticalAlign: 'middle',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'middle',
};

const monoCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  fontFamily: 'var(--font-mono)',
};

const tabButtonStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 26,
  padding: '0 10px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};

const tabButtonActiveStyle: React.CSSProperties = {
  ...tabButtonStyle,
  background: 'var(--accent-soft, var(--elevated))',
  color: 'var(--text-primary)',
  border: '1px solid var(--text-tertiary)',
};

const compactMetricStyle: React.CSSProperties = {
  minWidth: 0,
  display: 'grid',
  gap: 3,
  padding: '6px 8px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
};

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatAcknowledgementRate(stats: SaveWarningChainStats): string {
  if (stats.warningCount === 0) return '-';
  return `${Math.round((stats.acknowledgedCount / stats.warningCount) * 100)}%`;
}

function sortChains(chains: ForgeDashboardChainInfo[]): ForgeDashboardChainInfo[] {
  const preferredOrder = new Map(FORGE_CHAIN_OPTIONS.map((chain, index) => [chain.id, index]));
  return [...chains].sort((left, right) => {
    const leftIndex = preferredOrder.get(left.chainId) ?? Number.MAX_SAFE_INTEGER;
    const rightIndex = preferredOrder.get(right.chainId) ?? Number.MAX_SAFE_INTEGER;
    return leftIndex - rightIndex || left.chainName.localeCompare(right.chainName);
  });
}

function formatSimilarity(similarity: number | null): string {
  if (similarity === null) return '-';
  return `${Math.round(similarity * 100)}%`;
}

function cropForEvidence(evidence: SaveWarningEvidenceCrop): MasterCropData {
  return { id: evidence.cropId, imageUrl: evidence.vizUrl ?? '' };
}

/** The warning's Master Label deep link: the backend-supplied URL, or a fallback built from
 * the subject item and crops. Returns null when there is nothing to link to. */
function masterLabelUrlForWarning(warning: PersistedSaveWarning): string | null {
  if (warning.matchedMasterLabelUrl) return warning.matchedMasterLabelUrl;
  if (!warning.itemNumber) return null;
  return buildMasterLabelUrl({
    chainId: warning.chainId,
    itemNumber: warning.itemNumber,
    cropIds: warning.subjectCropIds,
  });
}

function warningIdentifierLabel(warning: PersistedSaveWarning): string {
  return warning.itemNumber ?? warning.itemKey ?? warning.warningId;
}

/** Required chain filter; mirrors CleaningTasksDashboard's local `chainIdParam`. */
function chainIdParam(): UrlParamCodec<number | null> {
  return {
    parse: (values) => {
      const parsed = Number.parseInt(values[0] ?? '', 10);
      return Number.isFinite(parsed) ? parsed : null;
    },
    serialize: (value) => (value === null ? [] : [String(value)]),
  };
}

const WARNINGS_URL_FILTERS = {
  chain: chainIdParam(),
  tool: enumParam(SAVE_CHECK_TOOLS, 'all'),
  checkType: enumParam(SAVE_CHECK_TYPES, 'all'),
  status: enumParam(SAVE_WARNING_STATUSES, 'all'),
  reviewOutcome: enumParam(SAVE_WARNING_REVIEW_OUTCOMES, 'all'),
  actor: stringParam(),
  createdAfter: stringParam(),
};

interface CheckTypeTally {
  total: number;
  acknowledged: number;
}

/** Dims the table with a "still working" badge during a refetch, so existing rows stay
 * visible instead of blanking into a loading row. Mirrors CleaningTasksDashboard. */
function TableBusyOverlay({ label }: { label: string }) {
  return (
    <div className="forge-busy-overlay" role="status" aria-live="polite">
      <span className="forge-busy-overlay-badge" data-testid="warnings-updating-indicator">
        {label}
      </span>
    </div>
  );
}

export const SaveWarningsDashboard: React.FC = () => {
  const { token, hasInternalAccess } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [filters, updateFilters] = useUrlFilterState(WARNINGS_URL_FILTERS);
  const [chains, setChains] = useState<ForgeDashboardChainInfo[]>(DEFAULT_SAVE_WARNING_CHAINS);
  const [chainStats, setChainStats] = useState<SaveWarningChainStats[]>([]);
  const [isChainStatsLoading, setIsChainStatsLoading] = useState(true);
  const [chainStatsError, setChainStatsError] = useState<string | null>(null);
  const chainStatsLoadSequence = useRef(0);
  const selectedChainId =
    filters.chain !== null && chains.some((chain) => chain.chainId === filters.chain)
      ? filters.chain
      : (chains[0]?.chainId ?? FORGE_CHAIN_OPTIONS[0].id);

  const loadChainStats = useCallback(async () => {
    const sequence = ++chainStatsLoadSequence.current;
    setIsChainStatsLoading(true);
    try {
      const supported = await ForgeApiService.fetchSupportedChains(token);
      const nextChains = sortChains(supported.chains);
      const stats = await ForgeApiService.fetchSaveWarningChainStats(
        token,
        nextChains.map((chain) => chain.chainId)
      );
      if (sequence !== chainStatsLoadSequence.current) return;
      setChains(nextChains);
      setChainStats(stats);
      setChainStatsError(null);
    } catch (error) {
      if (sequence !== chainStatsLoadSequence.current) return;
      const message =
        error instanceof Error ? error.message : 'Error loading save-warning chain statistics';
      setChainStatsError(message);
      showErrorToast(message);
    } finally {
      if (sequence === chainStatsLoadSequence.current) setIsChainStatsLoading(false);
    }
  }, [token, showErrorToast]);

  useEffect(() => {
    void loadChainStats();
  }, [loadChainStats]);

  useEffect(() => {
    if (filters.chain !== null && !chains.some((chain) => chain.chainId === filters.chain)) {
      updateFilters({ chain: chains[0]?.chainId ?? FORGE_CHAIN_OPTIONS[0].id });
    }
  }, [chains, filters.chain, updateFilters]);

  const committedActor = filters.actor;
  const [actorInput, setActorInput] = useState(committedActor);
  useEffect(() => {
    setActorInput(committedActor);
  }, [committedActor]);

  const [warnings, setWarnings] = useState<PersistedSaveWarning[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const warningsLoadSequence = useRef(0);

  const loadWarnings = useCallback(
    async (cursor?: string) => {
      // Guard against out-of-order responses: rapid chain/filter changes fire
      // overlapping requests, and a slower earlier one must not overwrite a
      // newer one. Only the latest-issued load applies its result.
      const sequence = ++warningsLoadSequence.current;
      if (cursor) setIsLoadingMore(true);
      else setIsLoading(true);
      try {
        const response = await ForgeApiService.fetchSaveWarnings(token, {
          chainId: selectedChainId,
          ...(filters.tool !== 'all' ? { tool: filters.tool } : {}),
          ...(filters.checkType !== 'all' ? { checkType: filters.checkType } : {}),
          ...(filters.status !== 'all' ? { status: filters.status } : {}),
          ...(filters.reviewOutcome !== 'all' ? { reviewOutcome: filters.reviewOutcome } : {}),
          ...(committedActor ? { actor: committedActor } : {}),
          ...(filters.createdAfter ? { createdAfter: filters.createdAfter } : {}),
          ...(cursor ? { cursor } : {}),
          limit: WARNINGS_PAGE_SIZE,
        });
        if (sequence !== warningsLoadSequence.current) return;
        setLoadError(null);
        setWarnings((current) => (cursor ? [...current, ...response.warnings] : response.warnings));
        setNextCursor(response.nextCursor);
      } catch (error) {
        if (sequence !== warningsLoadSequence.current) return;
        const message =
          error instanceof Error ? error.message : 'Error loading save-check warnings';
        setLoadError(message);
        if (!cursor) setWarnings([]);
        showErrorToast(message);
      } finally {
        if (sequence === warningsLoadSequence.current) {
          if (cursor) setIsLoadingMore(false);
          else setIsLoading(false);
        }
      }
    },
    [
      token,
      selectedChainId,
      filters.tool,
      filters.checkType,
      filters.status,
      filters.reviewOutcome,
      committedActor,
      filters.createdAfter,
      showErrorToast,
    ]
  );

  useEffect(() => {
    void loadWarnings();
  }, [loadWarnings]);

  const handleLoadMore = () => {
    if (!nextCursor || isLoadingMore) return;
    void loadWarnings(nextCursor);
  };

  const handleActorSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateFilters({ actor: actorInput.trim() });
  };

  const clearActor = () => {
    setActorInput('');
    updateFilters({ actor: '' });
  };

  const handleSelectChain = (chainId: number) => {
    updateFilters({ chain: chainId });
  };

  const [modes, setModes] = useState<SaveCheckModesResponse | null>(null);
  const [updatingTools, setUpdatingTools] = useState<Set<SaveCheckTool>>(() => new Set());
  const modesLoadSequence = useRef(0);
  const modeUpdateSequence = useRef<Record<SaveCheckTool, number>>({
    master_label: 0,
    remote_dc: 0,
  });
  const selectedChainIdRef = useRef(selectedChainId);

  useEffect(() => {
    selectedChainIdRef.current = selectedChainId;
    setModes(null);
    setUpdatingTools(new Set());
    modeUpdateSequence.current.master_label += 1;
    modeUpdateSequence.current.remote_dc += 1;
  }, [selectedChainId]);

  const loadModes = useCallback(async () => {
    // Same out-of-order guard as loadWarnings: a stale modes response from a
    // previously-selected chain must not clobber the current chain's modes.
    const sequence = ++modesLoadSequence.current;
    try {
      const response = await ForgeApiService.fetchSaveCheckModes(token, selectedChainId);
      if (sequence !== modesLoadSequence.current) return;
      setModes(response);
    } catch (error) {
      if (sequence !== modesLoadSequence.current) return;
      showErrorToast(
        error instanceof Error ? error.message : 'Error loading save-check enforcement modes'
      );
    }
  }, [token, selectedChainId, showErrorToast]);

  useEffect(() => {
    void loadModes();
  }, [loadModes]);

  const handleChangeMode = async (tool: SaveCheckTool, mode: EnforcementMode) => {
    if (
      !modes ||
      modes.chainId !== selectedChainId ||
      modes.modes[tool] === mode ||
      updatingTools.has(tool)
    ) {
      return;
    }
    const chainId = selectedChainId;
    const sequence = ++modeUpdateSequence.current[tool];
    setUpdatingTools((current) => new Set(current).add(tool));
    try {
      const response = await ForgeApiService.setSaveCheckMode(token, {
        chainId,
        tool,
        mode,
      });
      if (sequence !== modeUpdateSequence.current[tool] || selectedChainIdRef.current !== chainId) {
        return;
      }
      // The API returns both tools, but only this tool was mutated. Merge that
      // field so a slower response cannot overwrite a newer concurrent update
      // to the other tool.
      setModes((current) =>
        current?.chainId === chainId
          ? { ...current, modes: { ...current.modes, [tool]: response.modes[tool] } }
          : current
      );
      showSuccessToast(`${SAVE_CHECK_TOOL_LABELS[tool]} enforcement set to ${mode}`);
    } catch (error) {
      if (sequence !== modeUpdateSequence.current[tool] || selectedChainIdRef.current !== chainId) {
        return;
      }
      showErrorToast(
        error instanceof Error ? error.message : 'Error updating save-check enforcement mode'
      );
    } finally {
      if (sequence === modeUpdateSequence.current[tool]) {
        setUpdatingTools((current) => {
          const next = new Set(current);
          next.delete(tool);
          return next;
        });
      }
    }
  };

  const chainStatsById = useMemo(
    () => new Map(chainStats.map((stats) => [stats.chainId, stats])),
    [chainStats]
  );
  const selectedChain =
    chains.find((chain) => chain.chainId === selectedChainId) ??
    ({
      chainId: selectedChainId,
      chainName: getForgeChainLabel(selectedChainId),
      timeZone: 'UTC',
    } as ForgeDashboardChainInfo);
  const selectedChainStats = chainStatsById.get(selectedChainId);
  const globalStats = useMemo(
    () =>
      chainStats.reduce(
        (total, stats) => ({
          warningCount: total.warningCount + stats.warningCount,
          occurrenceCount: total.occurrenceCount + stats.occurrenceCount,
          reviewedCount:
            total.reviewedCount +
            stats.correctWarningCount +
            stats.falsePositiveCount +
            stats.unsureCount,
        }),
        { warningCount: 0, occurrenceCount: 0, reviewedCount: 0 }
      ),
    [chainStats]
  );
  const globalReviewRate =
    globalStats.warningCount > 0
      ? `${Math.round((globalStats.reviewedCount / globalStats.warningCount) * 100)}%`
      : '-';

  const summaryByCheckType = useMemo(() => {
    const tally = new Map<SaveCheckType, CheckTypeTally>();
    for (const warning of warnings) {
      const entry = tally.get(warning.checkType) ?? { total: 0, acknowledged: 0 };
      entry.total += 1;
      if (warning.status === 'acknowledged') entry.acknowledged += 1;
      tally.set(warning.checkType, entry);
    }
    return tally;
  }, [warnings]);

  const firedCount = warnings.filter((warning) => warning.status === 'fired').length;
  const acknowledgedCount = warnings.length - firedCount;
  const reviewedCount = warnings.filter((warning) => Boolean(warning.reviewOutcome)).length;
  const selectedCoverage = selectedChainStats?.coverage;
  const selectedRetrievalFailures = selectedCoverage
    ? selectedCoverage.cropRetrievalFailureCount + selectedCoverage.groupRetrievalFailureCount
    : 0;
  const selectedTimeouts = selectedCoverage
    ? selectedCoverage.cropTimeoutCount + selectedCoverage.groupTimeoutCount
    : 0;

  const summaryMetrics: Array<[string, number | string]> = [
    ['Chains', chainStats.length || chains.length],
    ['Unique warnings', globalStats.warningCount],
    ['Occurrences', globalStats.occurrenceCount],
    ['Reviewed', globalReviewRate],
  ];

  const columnCount = 8;
  // A refetch while rows are already shown dims the table instead of blanking it;
  // the very first load (no rows yet) still shows the "Loading warnings..." row.
  const isRefetching = isLoading && warnings.length > 0;

  return (
    <div className="page-content page-content--wide rdc-dashboard-page">
      <div className="forge-card rdc-dashboard-hero">
        <div className="rdc-dashboard-hero-grid">
          <div>
            <div className="rdc-dashboard-eyebrow">Dashboards</div>
            <div className="rdc-dashboard-title">Save-check warnings</div>
            <div className="rdc-dashboard-copy">
              Warnings fired by Master Label and Remote DC save checks before a crop or design-group
              change is saved. Review the evidence, then tune each tool&apos;s enforcement mode per
              chain.
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))',
              gap: 8,
            }}
          >
            {summaryMetrics.map(([label, value]) => (
              <div key={label} style={compactMetricStyle}>
                <div
                  style={{
                    overflow: 'hidden',
                    color: 'var(--text-tertiary)',
                    fontSize: 10,
                    fontWeight: 850,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 16,
                    fontWeight: 900,
                    lineHeight: 1.05,
                  }}
                >
                  {typeof value === 'number' ? formatNumber(value) : value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="forge-card rdc-dashboard-card" data-testid="save-warning-chains-card">
        <div
          className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
          style={{
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <span>Chains</span>
          <button
            type="button"
            aria-label="Refresh chain statistics"
            disabled={isChainStatsLoading}
            onClick={() => void loadChainStats()}
            style={{
              ...tabButtonStyle,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              opacity: isChainStatsLoading ? 0.65 : 1,
              cursor: isChainStatsLoading ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshOutlinedIcon
              className={isChainStatsLoading ? 'is-spinning' : ''}
              style={{ fontSize: 15 }}
            />
            {isChainStatsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Chain</th>
                <th style={headerCellStyle}>Warnings</th>
                <th style={headerCellStyle}>Status</th>
                <th style={headerCellStyle}>Check mix</th>
                <th style={headerCellStyle}>Latest</th>
              </tr>
            </thead>
            <tbody>
              {isChainStatsLoading && chainStats.length === 0 ? (
                <tr>
                  <td colSpan={5} style={bodyCellStyle}>
                    Loading chain statistics...
                  </td>
                </tr>
              ) : chainStatsError && chainStats.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...bodyCellStyle, color: 'var(--danger)' }}>
                    {chainStatsError}
                  </td>
                </tr>
              ) : (
                chains.map((chain) => {
                  const stats = chainStatsById.get(chain.chainId);
                  const isSelected = chain.chainId === selectedChainId;
                  return (
                    <tr
                      key={chain.chainId}
                      data-testid="save-warning-chain-row"
                      aria-selected={isSelected}
                      tabIndex={0}
                      title="Show this chain's warnings below"
                      style={{
                        cursor: 'pointer',
                        background: isSelected
                          ? 'color-mix(in srgb, var(--accent) 7%, transparent)'
                          : undefined,
                        boxShadow: isSelected ? 'inset 2px 0 0 var(--accent)' : undefined,
                      }}
                      onClick={() => handleSelectChain(chain.chainId)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSelectChain(chain.chainId);
                        }
                      }}
                    >
                      <td style={bodyCellStyle}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                          {chain.chainName}
                        </div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                          chain {chain.chainId}
                        </div>
                      </td>
                      <td style={bodyCellStyle}>
                        {stats ? (
                          <>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                              {formatNumber(stats.warningCount)} unique
                            </div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                              {formatNumber(stats.occurrenceCount)} occurrences
                            </div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={bodyCellStyle}>
                        {stats ? (
                          <>
                            <div style={{ color: STATUS_COLORS.fired, fontWeight: 800 }}>
                              {formatNumber(stats.firedCount)} fired
                            </div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                              {formatAcknowledgementRate(stats)} acknowledged ·{' '}
                              {formatNumber(
                                stats.correctWarningCount +
                                  stats.falsePositiveCount +
                                  stats.unsureCount
                              )}{' '}
                              reviewed
                            </div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={bodyCellStyle}>
                        {stats ? (
                          <>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                              {formatNumber(stats.cropPlacementCount)} crop placement
                            </div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                              {formatNumber(stats.designGroupCount)} design group
                            </div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={bodyCellStyle}>
                        {stats?.latestWarningAt ? (
                          <>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                              {formatRelativeAge(ageSecondsFromTimestamp(stats.latestWarningAt))}
                            </div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                              {formatTimestamp(stats.latestWarningAt)}
                            </div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 8, color: 'var(--text-tertiary)', fontSize: 11 }}>
          Warnings count unique warning identities; occurrences include repeat fires of the same
          warning. Select a chain to review its modes and evidence below.
        </div>
      </div>

      <div className="forge-card rdc-dashboard-card" data-testid="save-check-modes-card">
        <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
          Enforcement mode — {selectedChain.chainName}
        </div>
        {!hasInternalAccess ? (
          <div
            data-testid="save-check-modes-readonly-note"
            style={{ marginBottom: 8, fontSize: 11, color: 'var(--text-tertiary)' }}
          >
            Read-only — admin access is required to change enforcement modes.
          </div>
        ) : null}
        <div style={{ display: 'grid', gap: 10 }}>
          {SAVE_CHECK_TOOLS.map((tool) => {
            const currentMode = modes?.chainId === selectedChainId ? modes.modes[tool] : null;
            const isUpdating = updatingTools.has(tool);
            const controlDisabled =
              isUpdating || !hasInternalAccess || modes?.chainId !== selectedChainId;
            return (
              <div
                key={tool}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: 12 }}>
                  {SAVE_CHECK_TOOL_LABELS[tool]}
                </span>
                <div
                  role="group"
                  aria-label={`${SAVE_CHECK_TOOL_LABELS[tool]} enforcement mode`}
                  style={{ display: 'flex', gap: 6 }}
                >
                  {ENFORCEMENT_MODE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={currentMode === option.value}
                      disabled={controlDisabled}
                      style={{
                        ...(currentMode === option.value ? tabButtonActiveStyle : tabButtonStyle),
                        opacity: controlDisabled ? 0.6 : 1,
                        cursor: controlDisabled ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => void handleChangeMode(tool, option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="forge-card rdc-dashboard-card" data-testid="save-check-coverage-card">
        <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
          Check coverage — {selectedChain.chainName}
        </div>
        {!selectedCoverage || selectedCoverage.runCount === 0 ? (
          <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
            No save-check coverage runs have been recorded for this chain yet.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 8,
                marginBottom: 10,
              }}
            >
              {[
                ['Runs', selectedCoverage.runCount],
                [
                  'Crops checked',
                  `${formatNumber(selectedCoverage.checkedCropCount)} / ${formatNumber(
                    selectedCoverage.eligibleCropCount
                  )}`,
                ],
                [
                  'Groups checked',
                  `${formatNumber(selectedCoverage.checkedGroupCount)} / ${formatNumber(
                    selectedCoverage.eligibleGroupCount
                  )}`,
                ],
                ['Warnings', selectedCoverage.warningCount],
              ].map(([label, value]) => (
                <div key={label} style={compactMetricStyle}>
                  <div
                    style={{
                      color: 'var(--text-tertiary)',
                      fontSize: 10,
                      fontWeight: 850,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 16,
                      fontWeight: 900,
                    }}
                  >
                    {typeof value === 'number' ? formatNumber(value) : value}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 6,
                color: 'var(--text-secondary)',
                fontSize: 11,
              }}
            >
              {[
                ['Missing embeddings', selectedCoverage.missingEmbeddingCropCount],
                ['Missing group centroids', selectedCoverage.missingCentroidGroupCount],
                ['Retrieval failures', selectedRetrievalFailures],
                ['Timeouts', selectedTimeouts],
                ['Crops dropped by limit', selectedCoverage.droppedCropCount],
                ['Runs without a model', selectedCoverage.noModelVersionCount],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 6,
                    background: Number(value) > 0 ? 'var(--warning-subtle)' : 'var(--bg)',
                  }}
                >
                  <span>{label}</span>
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>
                    {formatNumber(Number(value))}
                  </strong>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, color: 'var(--text-tertiary)', fontSize: 10 }}>
              Aggregated by the backend from bounded daily chain/tool/config buckets
              {selectedChainStats?.coverageLastRunAt
                ? ` · last run ${formatRelativeAge(
                    ageSecondsFromTimestamp(selectedChainStats.coverageLastRunAt)
                  )}`
                : ''}
              .
            </div>
          </>
        )}
      </div>

      {warnings.length > 0 ? (
        <div className="forge-card rdc-dashboard-card" data-testid="save-warnings-summary-card">
          <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
            {selectedChain.chainName} calibration snapshot
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))',
              gap: 8,
              marginBottom: 12,
            }}
          >
            {[
              ['Loaded', warnings.length],
              ['Fired', firedCount],
              ['Acknowledged', acknowledgedCount],
              ['Reviewed', reviewedCount],
            ].map(([label, value]) => (
              <div key={label} style={compactMetricStyle}>
                <div
                  style={{
                    color: 'var(--text-tertiary)',
                    fontSize: 10,
                    fontWeight: 850,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 16,
                    fontWeight: 900,
                  }}
                >
                  {formatNumber(Number(value))}
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              marginBottom: 8,
              color: 'var(--text-tertiary)',
              fontSize: 10,
              fontWeight: 850,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Fired vs acknowledged by check type
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {SAVE_CHECK_TYPES.filter((checkType) => summaryByCheckType.has(checkType)).map(
              (checkType) => {
                const tally = summaryByCheckType.get(checkType) ?? { total: 0, acknowledged: 0 };
                const rate =
                  tally.total > 0 ? Math.round((tally.acknowledged / tally.total) * 100) : null;
                return (
                  <div
                    key={checkType}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {SAVE_CHECK_TYPE_LABELS[checkType]}
                    </span>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                      {formatNumber(tally.acknowledged)} / {formatNumber(tally.total)} acknowledged
                      {rate !== null ? ` (${rate}%)` : ''}
                    </span>
                  </div>
                );
              }
            )}
          </div>
          {reviewedCount > 0 ? (
            <>
              <div
                style={{
                  margin: '12px 0 8px',
                  color: 'var(--text-tertiary)',
                  fontSize: 10,
                  fontWeight: 850,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Explicit review outcomes
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {SAVE_WARNING_REVIEW_OUTCOMES.map((outcome) => {
                  const count = warnings.filter(
                    (warning) => warning.reviewOutcome === outcome
                  ).length;
                  return (
                    <div key={outcome} style={compactMetricStyle}>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
                        {REVIEW_OUTCOME_LABELS[outcome]}
                      </span>
                      <strong style={{ fontFamily: 'var(--font-mono)' }}>
                        {formatNumber(count)}
                      </strong>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-tertiary)' }}>
            Based on the {formatNumber(warnings.length)} warnings currently loaded.
          </div>
        </div>
      ) : null}

      <div className="forge-card rdc-dashboard-card" data-testid="save-warnings-card">
        <div
          className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
          style={{ marginBottom: 12, display: 'grid', gap: 10 }}
        >
          <span>Warnings — {selectedChain.chainName}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div className="dg-filter-group">
              <FilterListOutlinedIcon />
              <select
                aria-label="Tool filter"
                value={filters.tool}
                onChange={(event) =>
                  updateFilters({ tool: event.target.value as SaveCheckTool | 'all' })
                }
              >
                <option value="all">All tools</option>
                {SAVE_CHECK_TOOLS.map((tool) => (
                  <option key={tool} value={tool}>
                    {SAVE_CHECK_TOOL_LABELS[tool]}
                  </option>
                ))}
              </select>
            </div>
            <div className="dg-filter-group">
              <FilterListOutlinedIcon />
              <select
                aria-label="Check type filter"
                value={filters.checkType}
                onChange={(event) =>
                  updateFilters({ checkType: event.target.value as SaveCheckType | 'all' })
                }
              >
                <option value="all">All check types</option>
                {SAVE_CHECK_TYPES.map((checkType) => (
                  <option key={checkType} value={checkType}>
                    {SAVE_CHECK_TYPE_LABELS[checkType]}
                  </option>
                ))}
              </select>
            </div>
            <div className="dg-filter-group">
              <FilterListOutlinedIcon />
              <select
                aria-label="Status filter"
                value={filters.status}
                onChange={(event) =>
                  updateFilters({ status: event.target.value as SaveWarningStatus | 'all' })
                }
              >
                <option value="all">All statuses</option>
                {SAVE_WARNING_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </div>
            <div className="dg-filter-group">
              <FilterListOutlinedIcon />
              <select
                aria-label="Review outcome filter"
                value={filters.reviewOutcome}
                onChange={(event) =>
                  updateFilters({
                    reviewOutcome: event.target.value as SaveWarningReviewOutcome | 'all',
                  })
                }
              >
                <option value="all">All review outcomes</option>
                {SAVE_WARNING_REVIEW_OUTCOMES.map((outcome) => (
                  <option key={outcome} value={outcome}>
                    {REVIEW_OUTCOME_LABELS[outcome]}
                  </option>
                ))}
              </select>
            </div>
            <div className="dg-filter-group">
              <EventOutlinedIcon />
              <input
                type="date"
                aria-label="Created after"
                value={filters.createdAfter}
                onChange={(event) => updateFilters({ createdAfter: event.target.value })}
              />
            </div>
            <form className="dg-list-controls" onSubmit={handleActorSubmit}>
              <div className="dg-search dg-search--compact">
                <SearchOutlinedIcon />
                <input
                  type="search"
                  aria-label="Filter by actor"
                  placeholder="Actor"
                  value={actorInput}
                  onChange={(event) => setActorInput(event.target.value)}
                />
              </div>
              {committedActor ? (
                <button type="button" className="dg-search-clear" onClick={clearActor}>
                  Clear
                </button>
              ) : null}
              <button type="submit" className="dg-primary-button">
                Search
              </button>
            </form>
          </div>
        </div>
        <div
          className={`forge-busy-container${isRefetching ? ' is-busy' : ''}`}
          aria-busy={isRefetching}
        >
          {isRefetching ? <TableBusyOverlay label="Updating warnings..." /> : null}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Item</th>
                  <th style={headerCellStyle}>Check</th>
                  <th style={headerCellStyle}>Tool</th>
                  <th style={headerCellStyle}>Status</th>
                  <th style={headerCellStyle}>Evidence</th>
                  <th style={headerCellStyle}>Message</th>
                  <th style={headerCellStyle}>Actor</th>
                  <th style={headerCellStyle}>Created</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && warnings.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} style={bodyCellStyle}>
                      Loading warnings...
                    </td>
                  </tr>
                ) : loadError && warnings.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} style={{ ...bodyCellStyle, color: 'var(--danger)' }}>
                      {loadError}
                    </td>
                  </tr>
                ) : warnings.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} style={bodyCellStyle}>
                      No warnings match this filter.
                    </td>
                  </tr>
                ) : (
                  warnings.map((warning) => {
                    const masterLabelUrl = masterLabelUrlForWarning(warning);
                    const identifier = warningIdentifierLabel(warning);
                    return (
                      <tr key={warning.warningId} data-testid="save-warning-row">
                        <td style={monoCellStyle}>
                          {masterLabelUrl ? (
                            <MasterLabelLink
                              to={masterLabelUrl}
                              variant="forge"
                              title="Open in Master Label"
                            >
                              {identifier}
                            </MasterLabelLink>
                          ) : (
                            <span>{identifier}</span>
                          )}
                        </td>
                        <td style={bodyCellStyle}>{SAVE_CHECK_TYPE_LABELS[warning.checkType]}</td>
                        <td style={bodyCellStyle}>{SAVE_CHECK_TOOL_LABELS[warning.tool]}</td>
                        <td style={bodyCellStyle}>
                          <span style={{ color: STATUS_COLORS[warning.status], fontWeight: 800 }}>
                            {STATUS_LABELS[warning.status]}
                          </span>
                          {warning.reviewOutcome ? (
                            <div
                              style={{ marginTop: 3, color: 'var(--text-tertiary)', fontSize: 10 }}
                            >
                              {REVIEW_OUTCOME_LABELS[warning.reviewOutcome]}
                            </div>
                          ) : null}
                        </td>
                        <td style={bodyCellStyle}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxWidth: 220 }}>
                            {warning.evidenceCrops.map((evidence) => (
                              <div
                                key={evidence.cropId}
                                style={{ display: 'grid', gap: 2, justifyItems: 'center' }}
                              >
                                <CropThumbnail
                                  crop={cropForEvidence(evidence)}
                                  sectionType="front"
                                  size={40}
                                />
                                <span
                                  style={{
                                    fontSize: 9,
                                    color: 'var(--text-tertiary)',
                                    fontFamily: 'var(--font-mono)',
                                  }}
                                >
                                  {formatSimilarity(evidence.similarity)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td style={bodyCellStyle}>
                          <div>{warning.message}</div>
                          {warning.acknowledgementComment ? (
                            <div
                              style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 4 }}
                            >
                              Ack: &ldquo;{warning.acknowledgementComment}&rdquo;
                              {warning.acknowledgedBy ? ` — ${warning.acknowledgedBy}` : ''}
                            </div>
                          ) : null}
                        </td>
                        <td style={bodyCellStyle}>{warning.actor}</td>
                        <td style={monoCellStyle}>{formatTimestamp(warning.createdAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 10,
            fontSize: 11,
            color: 'var(--text-tertiary)',
          }}
        >
          <span>
            {formatNumber(warnings.length)} warnings loaded
            {!nextCursor && warnings.length > 0 ? ' · end of results' : ''}
          </span>
          <button
            type="button"
            className="dg-secondary-button"
            disabled={!nextCursor || isLoading || isLoadingMore}
            onClick={handleLoadMore}
          >
            {isLoadingMore ? 'Loading more...' : 'Load more'}
          </button>
        </div>
      </div>
    </div>
  );
};
